import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef } from 'react';
import type { StickyNoteProps, StickyNoteRef } from './StickyNote.data';
import styles from './StickyNote.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './StickyNote.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import Heading from 'components/atoms/heading/Heading';
import Text from 'components/atoms/text/Text';
import type { HeadingRef } from 'components/atoms/heading/Heading.data';
import type { TextRef } from 'components/atoms/text/Text.data';

export default function StickyNote({
  isVisible,
  className,
  note,
  index,
  ref,
}: StickyNoteProps & { ref?: Ref<StickyNoteRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement>;

  const isVisibleInViewport = useIntersectionObserver(elementRef, {
    threshold: [0, 0.5],
  });

  const titleRef = useRef<HeadingRef>(null);
  const textRef = useRef<TextRef>(null);

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
        titleRef,
        textRef,
        index,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, {
    onMount: isVisible?.onMount ?? true,
    value: (isVisible?.value ?? true) && isVisibleInViewport,
  });

  return (
    <div ref={elementRef} className={classNames(styles.stickyNote, className)}>
      <Heading ref={titleRef} className={classNames(styles.title)} as="h4" size="heading-04">
        {note.title}
      </Heading>
      {note.text && (
        <Text ref={textRef} className={classNames(styles.text)} as="p" size="body-standard">
          {note.text}
        </Text>
      )}
    </div>
  );
}
